<template>
  <!-- Global Database Popup Windows -->
  <template v-for="popup in popupStore.popupTables" :key="popup.id">
    <div
      v-if="popup.show"
      @mousedown="bringPopupToFront(popup.id)"
      class="fixed bg-white/50 rounded-lg shadow-2xl flex flex-col overflow-hidden"
      style="transition: opacity 0.3s, box-shadow 0.3s;"
      :class="{ 
        'h-auto': popup.minimized,
        'ring-2 ring-blue-500 ring-opacity-50': activePopupId === popup.id,
        'rounded-b-none': popup.chatMode && !popup.minimized,
        'rounded-t-lg': popup.chatMode && popup.minimized,
        'shadow-xl border-t border-gray-200': popup.chatMode
      }"
      :style="{
        left: popup.chatMode && popup.minimized ? getChatMinimizedPosition(popup.id).x + 'px' : popup.x + 'px',
        top: popup.chatMode && popup.minimized ? getChatMinimizedPosition(popup.id).y + 'px' : popup.y + 'px',
        width: popup.chatMode && popup.minimized ? '570px' : popup.width + 'px',
        height: popup.minimized ? 'auto' : popup.height + 'px',
        zIndex: popup.chatMode ? 10000 + getChatModeIndex(popup.id) : getPopupZIndex(popup.id)
      }"
    >
      <!-- Draggable Header -->
      <div 
        class="bg-blue-600 px-4 py-3 flex items-center justify-between border-b border-blue-700 flex-shrink-0 select-none relative overflow-hidden"
        :class="popup.chatMode ? 'cursor-default' : 'cursor-move'"
        @mousedown="popup.chatMode ? null : popupStore.startDrag($event, popup.id)"
      >
        <div class="flex items-center">
          <i class="fas fa-database text-white mr-2"></i>
          <h3 class="text-sm font-semibold text-white">{{ popup.title }}</h3>
          <span class="ml-3 px-2 py-1 bg-blue-800 text-blue-100 text-xs rounded">DB Manager</span>
          
          <!-- Popup Sidebar Width Control - only show when not in chat mode or when chat mode has enough space -->
          <div v-if="popupSidebarStates[popup.id]?.collapsed === false && (!popup.chatMode || popup.width >= 600)" class="ml-4 flex items-center text-xs text-blue-100">
            <label class="mr-2">Panel:</label>
            <input
              v-model.number="popupSidebarWidths[popup.id]"
              type="number"
              min="200"
              max="600"
              class="w-12 px-1 py-0.5 text-xs border border-blue-500 bg-blue-700 text-blue-100 placeholder-blue-300 rounded focus:ring-1 focus:ring-blue-300 focus:border-blue-300"
              @input="validatePopupSidebarWidth(popup.id)"
            />
            <span class="ml-1 text-xs">px</span>
          </div>
        </div>
                  <div class="flex items-center space-x-1 flex-shrink-0">
            <!-- Toggle All Popups -->
            <button
              v-if="popupStore.popupTables.length > 1"
              @click="popupStore.toggleAllPopupsVisibility"
              class="text-blue-100 hover:text-white p-1.5 hover:bg-blue-700 rounded relative"
              :title="popupStore.allPopupsHidden ? `Show All Popups (${popupStore.popupTables.length})` : `Hide All Popups (${popupStore.getVisiblePopupsCount()})`"
            >
              <i :class="popupStore.allPopupsHidden ? 'fas fa-eye' : 'fas fa-eye-slash'" class="text-sm"></i>
              <span class="absolute -top-1 -right-1 bg-blue-200 text-blue-900 text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                {{ popupStore.allPopupsHidden ? popupStore.popupTables.length : popupStore.getVisiblePopupsCount() }}
              </span>
            </button>
            
            <button
              @click="togglePopupSidebar(popup.id)"
              class="text-blue-100 hover:text-white p-1.5 hover:bg-blue-700 rounded"
              :title="popupSidebarStates[popup.id]?.collapsed === false ? 'Hide Table List' : 'Show Table List'"
            >
              <i class="fas fa-list text-sm"></i>
            </button>
            
            <button
              @click="popupStore.toggleChatMode(popup.id)"
              class="text-blue-100 hover:text-white p-1.5 hover:bg-blue-700 rounded"
              :class="{ 'text-white bg-blue-800': popup.chatMode }"
              :title="popup.chatMode ? 'Exit Chat Mode' : 'Enter Chat Mode'"
            >
              <i class="fas fa-comments text-sm"></i>
            </button>
            <button
              v-if="popup.type === 'data'"
              @click="togglePopupPanel(popup.id)"
              class="text-blue-100 hover:text-white p-1.5 hover:bg-blue-700 rounded"
              :title="popupPanelStates[popup.id]?.collapsed === false ? 'Hide Info Panel' : 'Show Info Panel'"
            >
              <i class="fas fa-info-circle text-sm"></i>
            </button>
            <button
              @click="togglePopupScale(popup.id)"
              class="text-blue-100 hover:text-white p-1.5 hover:bg-blue-700 rounded relative"
              :title="`Current scale: ${getPopupScale(popup.id)}x - Click to cycle through 1x/2x/3x/4x`"
            >
              <i class="fas fa-expand-arrows-alt text-sm"></i>
              <span class="absolute -top-1 -right-1 bg-blue-200 text-blue-900 text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                {{ getPopupScale(popup.id) }}
              </span>
            </button>
            <button
              @click="popupStore.minimizePopup(popup.id)"
              class="text-blue-100 hover:text-white p-1.5 hover:bg-blue-700 rounded"
              title="Minimize"
            >
              <i class="fas fa-minus text-sm"></i>
            </button>
            <button
              @click="popupStore.toggleMaximizePopup(popup.id)"
              class="text-blue-100 hover:text-white p-1.5 hover:bg-blue-700 rounded"
              title="Maximize/Restore"
            >
              <i class="fas fa-expand text-sm"></i>
            </button>
            <button
              v-if="popup.type === 'data'"
              @click="toggleSqlMode(popup.id)"
              class="text-blue-100 hover:text-white p-1.5 hover:bg-blue-700 rounded"
              :class="{ 'text-white bg-blue-800': popupSqlMode[popup.id]?.enabled }"
              :title="popupSqlMode[popup.id]?.enabled ? 'Switch to Table View' : 'Switch to SQL Query Mode'"
            >
              <i class="fas fa-code text-sm"></i>
            </button>
            <button
              @click="testDiagnostics(popup.id)"
              class="text-blue-100 hover:text-white p-1.5 hover:bg-blue-700 rounded"
              title="Test Diagnostics"
            >
              <i class="fas fa-bug text-sm"></i>
            </button>
            <button
              @click="popupStore.closePopup(popup.id)"
              class="text-blue-100 hover:text-red-300 p-1.5 hover:bg-red-600 rounded"
              title="Close"
            >
              <i class="fas fa-times text-sm"></i>
            </button>
          </div>
      </div>

      <!-- Popup Content -->
      <div v-if="!popup.minimized" class="flex-1 overflow-hidden flex relative">
        <!-- Left Sidebar - Table List -->
        <div 
          v-if="popupSidebarStates[popup.id]?.collapsed === false"
          :style="{ width: (popupSidebarWidths[popup.id] || 256) + 'px' }"
          class="bg-gray-50 border-r border-gray-200 overflow-y-auto flex-shrink-0"
        >
          <TableSelector 
            :selected-table="popup.tableName"
            :grid-mode="false"
            :grid-tabs="[]"
            :grid-layout="'2x2'"
            @table-select="(tableName) => handlePopupDataView(popup.id, tableName)"
            @table-deleted="() => {}"
            @open-structure="(tableName) => handlePopupStructureView(popup.id, tableName)"
            @open-data="(tableName) => handlePopupDataView(popup.id, tableName)"
            @open-popup="(tableName, type) => popupStore.addPopupTable(tableName, type)"
          />
        </div>
        
        <!-- Resize Handle -->
        <div 
          v-if="popupSidebarStates[popup.id]?.collapsed === false"
          class="w-1 bg-transparent hover:bg-gray-300 cursor-col-resize flex-shrink-0 transition-colors duration-150 group"
          :class="{ 'bg-gray-400': popupIsResizing[popup.id] }"
          @mousedown="startPopupResize($event, popup.id)"
          title="Drag to resize sidebar"
        >
          <!-- Visible indicator only on hover -->
          <div class="h-full w-full bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity duration-150" :class="{ 'opacity-100': popupIsResizing[popup.id] }"></div>
        </div>

        <!-- Main Content Area -->
        <div class="flex-1 bg-gray-50 overflow-hidden flex min-h-0">
          <!-- Table Content -->
          <div class="flex-1 overflow-hidden min-h-0" 
               :class="popupRecordViewers[popup.id]?.show ? 'w-2/3' : 'w-full'">
            <!-- Default state when no table selected - Show table list -->
            <div v-if="!popup.tableName" class="h-full overflow-hidden">
              <div class="h-full bg-white">
                <div class="p-6 bg-gradient-to-br from-gray-50 to-white rounded-lg shadow-sm border border-gray-100">
                  <div class="flex items-center space-x-3 mb-3">
                    <div class="p-2 bg-blue-50 rounded-lg">
                      <i class="fas fa-database text-blue-500 text-lg"></i>
                    </div>
                    <h3 class="text-xl font-semibold text-gray-800">Select a Table</h3>
                  </div>
                  <p class="text-sm text-gray-500 leading-relaxed">
                    Browse and select a table to explore its data structure or view its contents. 
                    You can also create new tables or modify existing ones.
                  </p>
                </div>
                <!-- <div class="h-full overflow-y-auto">
                  <TableSelector 
                    :selected-table="''"
                    :grid-mode="false"
                    :grid-tabs="[]"
                    :grid-layout="'2x2'"
                    :auto-focus="true"
                    @table-select="(tableName) => handlePopupDataView(popup.id, tableName)"
                    @table-deleted="() => {}"
                    @open-structure="(tableName) => handlePopupStructureView(popup.id, tableName)"
                    @open-data="(tableName) => handlePopupDataView(popup.id, tableName)"
                    @open-popup="(tableName, type) => popupStore.addPopupTable(tableName, type)"
                  />
                </div> -->
              </div>
            </div>
            
            <!-- Table content when table is selected -->
            <template v-else>
              <TableStructure 
                v-if="popup.type === 'structure'"
                :table-name="popup.tableName" 
              />
              
              <!-- SQL Query Mode -->
              <div v-else-if="popupSqlMode[popup.id]?.enabled" class="flex flex-col h-full">
                <!-- SQL Query Header -->
                <div class="p-3 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                  <div class="flex items-center justify-between mb-2">
                    <h3 class="text-sm font-semibold text-gray-900 flex items-center">
                      <i class="fas fa-code text-blue-600 mr-2"></i>
                      SQL Query Mode
                    </h3>
                    <div class="flex items-center space-x-2">
                      <button
                        @click="executeSqlQuery(popup.id)"
                        :disabled="!popupSqlMode[popup.id]?.query?.trim()"
                        class="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-3 py-1 rounded text-xs flex items-center"
                      >
                        <i class="fas fa-play mr-1"></i>
                        Execute
                      </button>
                      <button
                        @click="clearSqlQuery(popup.id)"
                        class="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-xs"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  
                  <!-- SQL Query Input -->
                  <div class="relative">
                    <div
                      :id="`sql-editor-${popup.id}`"
                      contenteditable="true"
                      class="w-full h-20 p-3 border border-gray-300 rounded font-mono text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent overflow-auto"
                      @input="handleSqlInput(popup.id, $event)"
                      @keydown="handleSqlKeydown(popup.id, $event)"
                      @focus="showSuggestions[popup.id] && (showSuggestions[popup.id] = showSuggestions[popup.id])"
                      @blur="handleSqlBlur(popup.id)"
                      style="white-space: pre-wrap;"
                    ></div>
                    
                    <!-- Placeholder for empty state -->
                    <div 
                      v-if="!popupSqlMode[popup.id]?.query" 
                      class="absolute top-3 left-3 text-gray-400 pointer-events-none font-mono text-sm"
                    >
                      Enter your SQL query here... (e.g., SELECT * FROM comparable_data LIMIT 10)
                    </div>
                    
                    <!-- Suggestions Dropdown -->
                    <div 
                      v-if="showSuggestions[popup.id] && showSuggestions[popup.id].length > 0"
                      class="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-b shadow-lg z-50 max-h-48 overflow-y-auto"
                    >
                      <div
                        v-for="(suggestion, index) in showSuggestions[popup.id]"
                        :key="index"
                        class="px-3 py-2 cursor-pointer hover:bg-blue-50 flex items-center justify-between"
                        :class="{ 'bg-blue-100': selectedSuggestionIndex[popup.id] === index }"
                        @click="clickSuggestion(popup.id, suggestion)"
                      >
                        <div class="flex items-center">
                          <span 
                            class="inline-block w-2 h-2 rounded-full mr-2"
                            :class="{
                              'bg-blue-500': suggestion.type === 'keyword',
                              'bg-green-500': suggestion.type === 'table',
                              'bg-purple-500': suggestion.type === 'query'
                            }"
                          ></span>
                          <span class="font-mono text-sm">{{ suggestion.value }}</span>
                        </div>
                        <span class="text-xs text-gray-500">{{ suggestion.description }}</span>
                      </div>
                    </div>
                    
                    <div class="absolute bottom-2 right-2 text-xs text-gray-400">
                      Ctrl+Enter to execute • Tab/Enter to accept suggestion
                    </div>
                  </div>
                </div>

                <!-- SQL Results -->
                <div class="flex-1 overflow-hidden">
                  <div v-if="popupSqlMode[popup.id]?.loading" class="flex items-center justify-center h-full">
                    <i class="fas fa-spinner fa-spin text-2xl text-gray-400"></i>
                  </div>
                  
                  <div v-else-if="popupSqlMode[popup.id]?.error" class="p-4">
                    <div class="bg-red-50 border border-red-200 rounded p-3">
                      <div class="flex items-center mb-2">
                        <i class="fas fa-exclamation-triangle text-red-600 mr-2"></i>
                        <span class="text-red-800 font-semibold">SQL Error</span>
                      </div>
                      <pre class="text-red-700 text-sm font-mono whitespace-pre-wrap">{{ popupSqlMode[popup.id].error }}</pre>
                    </div>
                  </div>
                  
                  <div v-else-if="popupSqlMode[popup.id]?.results" class="h-full overflow-auto">
                    <div class="p-3 bg-green-50 border-b border-green-200 text-sm">
                      <i class="fas fa-check-circle text-green-600 mr-2"></i>
                      Query executed successfully. 
                      <span class="font-semibold">{{ popupSqlMode[popup.id].rowCount || popupSqlMode[popup.id].results.length }}</span> rows returned
                      <span v-if="popupSqlMode[popup.id].executionTime" class="ml-2 text-gray-600">
                        ({{ popupSqlMode[popup.id].executionTime }}ms)
                      </span>
                    </div>
                    
                    <table v-if="popupSqlMode[popup.id].results.length > 0" class="w-full divide-y divide-gray-200 text-xs" style="min-width: max-content;">
                      <thead class="bg-gray-50 sticky top-0">
                        <tr>
                          <th
                            v-for="column in Object.keys(popupSqlMode[popup.id].results[0])"
                            :key="column"
                            class="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200"
                          >
                            {{ column }}
                          </th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-100">
                        <tr 
                          v-for="(row, index) in popupSqlMode[popup.id].results" 
                          :key="index" 
                          class="hover:bg-gray-50 cursor-pointer"
                          @click="handleSqlResultRowClick(popup.id, row)"
                        >
                          <td
                            v-for="column in Object.keys(row)"
                            :key="column"
                            class="px-2 py-0.5 whitespace-nowrap text-xs text-gray-900 border-r border-gray-100"
                          >
                            {{ row[column] }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <div v-else class="flex items-center justify-center h-full text-gray-500">
                    <div class="text-center">
                      <i class="fas fa-database text-4xl mb-4"></i>
                      <p class="text-sm">Enter a SQL query above and click Execute</p>
                      <p class="text-xs text-gray-400 mt-1">Try: SELECT * FROM {{ popup.tableName }} LIMIT 10</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Normal Table Data View -->
              <TableData 
                v-else
                :table-name="popup.tableName"
                :popup-id="popup.id"
                :persisted-filters="popupTableFilters[popup.id]?.[popup.tableName]"
                @record-selected="(record, columns) => handlePopupRecordSelected(popup.id, record, columns, popup.tableName)"
                @record-deselected="() => handlePopupRecordDeselected(popup.id)"
                @table-changed="(tableName) => handlePopupDataView(popup.id, tableName)"
                @diagnostics-toggle="handleDiagnosticsToggle.bind(null, popup.id)"
                @filters-changed="(filters) => handleFiltersChanged(popup.id, popup.tableName, filters)"
              />
            </template>
          </div>

          <!-- Inline Record Viewer -->
          <div 
            v-if="popupRecordViewers[popup.id]?.show" 
            class="w-1/3 border-l border-gray-200 bg-gray-50 overflow-hidden min-h-0 flex flex-col"
          >
            <RecordViewer
              :show="true"
              :record="popupRecordViewers[popup.id].record"
              :columns="popupRecordViewers[popup.id].columns"
              :table-name="popupRecordViewers[popup.id].tableName"
              :is-inline="true"
              @close="() => closePopupRecordViewer(popup.id)"
              @edit="() => editPopupSelectedRecord(popup.id)"
            />
          </div>
        </div>

        <!-- Right Info Panel -->
        <TableInfoPanel 
          v-if="popup.type === 'data' && popupPanelStates[popup.id]?.collapsed === false"
          :table-name="popup.tableName"
          :collapsed="false"
          @toggle-collapse="() => togglePopupPanel(popup.id)"
          class="flex-shrink-0"
        />

        <!-- Terminal-style Diagnostics Panel OVERLAY inside popup -->
        <div 
          class="absolute inset-0 bg-black text-green-400 font-mono text-sm shadow-2xl z-50"
          :style="{ display: popupDiagnostics[popup.id]?.show ? 'block' : 'none' }"
        >
          <!-- Terminal Header -->
          <div class="bg-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-600">
            <div class="flex items-center space-x-2">
              <div class="flex space-x-1">
                <div class="w-3 h-3 bg-red-500 rounded-full"></div>
                <div class="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <div class="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
              <span class="text-gray-300 text-xs">Database Query Diagnostics</span>
            </div>
            <button
              @click="popupDiagnostics[popup.id].show = false"
              class="text-gray-400 hover:text-white transition-colors"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          
          <!-- Terminal Content -->
          <div class="p-4 overflow-y-auto h-full">
            <div v-if="popupDiagnostics[popup.id]?.diagnostics">
              <!-- Terminal prompt and summary -->
              <div class="mb-4">
                <div class="text-green-400">$ query-diagnostics --table={{ popupDiagnostics[popup.id]?.tableName }}</div>
                <div class="text-blue-400">
                  ┌─ Execution Summary ─────────────────────────────────────────┐
                </div>
                <div class="text-white">
                  │ Total Time: <span class="text-yellow-400">{{ popupDiagnostics[popup.id]?.diagnostics.total_time }}ms</span>
                  │ Mode: <span :class="popupDiagnostics[popup.id]?.diagnostics.joins_enabled ? 'text-green-400' : 'text-red-400'">
                    {{ popupDiagnostics[popup.id]?.diagnostics.joins_enabled ? 'JOINs Enabled' : 'Raw Mode' }}
                  </span>
                  │ Foreign Keys: <span class="text-cyan-400">{{ popupDiagnostics[popup.id]?.diagnostics.foreign_keys_count || 0 }}</span>
                </div>
                <div class="text-blue-400">
                  └─────────────────────────────────────────────────────────────┘
                </div>
              </div>

              <!-- Performance breakdown -->
              <div class="mb-4">
                <div class="text-green-400">$ breakdown --verbose</div>
                
                <div v-if="popupDiagnostics[popup.id]?.diagnostics.table_structure_query" class="text-gray-300">
                  <span class="text-cyan-400">├─</span> table_structure_query: <span class="text-yellow-400">{{ popupDiagnostics[popup.id]?.diagnostics.table_structure_query }}ms</span>
                </div>
                
                <div v-if="popupDiagnostics[popup.id]?.diagnostics.column_formatting" class="text-gray-300">
                  <span class="text-cyan-400">├─</span> column_formatting: <span class="text-yellow-400">{{ popupDiagnostics[popup.id]?.diagnostics.column_formatting }}ms</span>
                </div>
                
                <div v-if="popupDiagnostics[popup.id]?.diagnostics.foreign_key_detection" class="text-gray-300">
                  <span class="text-cyan-400">├─</span> foreign_key_detection: <span class="text-yellow-400">{{ popupDiagnostics[popup.id]?.diagnostics.foreign_key_detection }}ms</span>
                </div>
                
                <div v-if="popupDiagnostics[popup.id]?.diagnostics.query_building" class="text-gray-300">
                  <span class="text-cyan-400">├─</span> query_building: <span class="text-yellow-400">{{ popupDiagnostics[popup.id]?.diagnostics.query_building }}ms</span>
                </div>
                
                <div v-if="popupDiagnostics[popup.id]?.diagnostics.search_conditions" class="text-gray-300">
                  <span class="text-cyan-400">├─</span> search_conditions: <span class="text-yellow-400">{{ popupDiagnostics[popup.id]?.diagnostics.search_conditions }}ms</span>
                </div>
                
                <div v-if="popupDiagnostics[popup.id]?.diagnostics.count_query" class="text-gray-300">
                  <span class="text-cyan-400">├─</span> <span class="text-orange-400 font-bold">count_query (SQL)</span>: <span class="text-red-400 font-bold">{{ popupDiagnostics[popup.id]?.diagnostics.count_query }}ms</span>
                </div>
                
                <div v-if="popupDiagnostics[popup.id]?.diagnostics.data_query" class="text-gray-300">
                  <span class="text-cyan-400">├─</span> <span class="text-orange-400 font-bold">data_query (SQL)</span>: <span class="text-red-400 font-bold">{{ popupDiagnostics[popup.id]?.diagnostics.data_query }}ms</span>
                </div>
                
                <div v-if="popupDiagnostics[popup.id]?.diagnostics.final_processing" class="text-gray-300">
                  <span class="text-cyan-400">└─</span> final_processing: <span class="text-yellow-400">{{ popupDiagnostics[popup.id]?.diagnostics.final_processing }}ms</span>
                </div>
              </div>

              <!-- Error if present -->
              <div v-if="popupDiagnostics[popup.id]?.diagnostics.error" class="mb-4">
                <div class="text-red-400">$ error --details</div>
                <div class="text-red-300 bg-red-900 bg-opacity-20 p-2 rounded border border-red-600">
                  <span class="text-red-400">ERROR:</span> {{ popupDiagnostics[popup.id]?.diagnostics.error }}
                </div>
              </div>

              <!-- Performance tips -->
              <div class="mb-4">
                <div class="text-green-400">$ performance-tips</div>
                <div class="text-gray-400 text-xs space-y-1">
                  <div><span class="text-cyan-400">•</span> SQL queries (count_query, data_query) are actual MySQL execution times</div>
                  <div><span class="text-cyan-400">•</span> High foreign_key_detection indicates complex table relationships</div>
                  <div><span class="text-cyan-400">•</span> search_conditions time increases with complex patterns</div>
                  <div><span class="text-cyan-400">•</span> Use Raw mode for faster queries on large datasets</div>
                </div>
              </div>

              <!-- Terminal prompt -->
              <div class="text-green-400">
                $ <span class="animate-pulse">█</span>
              </div>
            </div>

            <div v-else class="text-center py-8 text-gray-500">
              <div class="text-red-400">$ diagnostics --status</div>
              <div class="text-gray-400">No diagnostics data available</div>
              <div class="text-gray-500 text-xs mt-2">Run a query to see performance metrics</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Resize Handle -->
      <div 
        v-if="!popup.minimized"
        class="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
        @mousedown="popupStore.startResize($event, popup.id)"
      >
        <div class="absolute bottom-1 right-1 w-2 h-2 border-r-2 border-b-2 border-gray-400"></div>
      </div>


    </div>
  </template>


</template>

<script setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { useDatabasePopupStore } from '../stores/databasePopupStore'
import TableData from '../views/server/components/TableData.vue'
import TableStructure from '../views/server/components/TableStructure.vue'
import RecordViewer from '../views/server/components/RecordViewer.vue'
import TableInfoPanel from '../views/server/components/TableInfoPanel.vue'
import TableSelector from '../views/server/components/TableSelector.vue'
import axios from '../axios.js'

const popupStore = useDatabasePopupStore()
const route = useRoute()

// Load UI state from localStorage
const loadUIStateFromLocalStorage = () => {
  try {
    const savedUIState = localStorage.getItem('databasePopupUIState')
    if (savedUIState) {
      const parsed = JSON.parse(savedUIState)
      return parsed
    }
  } catch (error) {
    console.warn('Failed to load UI state from localStorage:', error)
  }
  return {}
}

// Save UI state to localStorage
const saveUIStateToLocalStorage = () => {
  try {
    const uiState = {
      panelStates: popupPanelStates.value,
      sidebarStates: popupSidebarStates.value,
      sqlMode: popupSqlMode.value,
      tableFilters: popupTableFilters.value,
      popupScales: popupScales.value,
      // Don't save temporary states like record viewers or diagnostics
    }
    localStorage.setItem('databasePopupUIState', JSON.stringify(uiState))
  } catch (error) {
    console.warn('Failed to save UI state to localStorage:', error)
  }
}

// Initialize UI state from localStorage
const initialUIState = loadUIStateFromLocalStorage()

// Panel state for each popup
const popupPanelStates = ref(initialUIState.panelStates || {})

// Sidebar state for each popup
const popupSidebarStates = ref(initialUIState.sidebarStates || {})

// Record viewer state for popup windows (per popup) - Not persisted as it's temporary
const popupRecordViewers = ref({})

// Diagnostics state for popup windows (per popup) - Not persisted as it's temporary
const popupDiagnostics = ref({})

// SQL query mode state for popup windows (per popup)
const popupSqlMode = ref(initialUIState.sqlMode || {})

// Table filters state for popup windows (per popup and table)
const popupTableFilters = ref(initialUIState.tableFilters || {})

// Popup scale state for popup windows (per popup)
const popupScales = ref(initialUIState.popupScales || {})

// Sidebar resize functionality for popup windows (per popup)
const popupSidebarWidths = ref({})
const popupIsResizing = ref({})
const popupResizeStartX = ref({})
const popupResizeStartWidth = ref({})

// Z-index management for popup layering
const activePopupId = ref(null)
const baseZIndex = 9999

// SQL suggestions and autocomplete - Not persisted as they're temporary
const sqlSuggestions = ref([])
const showSuggestions = ref({})
const selectedSuggestionIndex = ref({})

// Watch for UI state changes and save to localStorage
watch([popupPanelStates, popupSidebarStates, popupSqlMode, popupTableFilters, popupScales], () => {
  saveUIStateToLocalStorage()
}, { deep: true })

// Initialize sidebar states for new popups
watch(() => popupStore.popupTables, (newPopups) => {
  newPopups.forEach(popup => {
    // Initialize sidebar state if not exists
    if (!popupSidebarStates.value[popup.id]) {
      // Open sidebar by default
      popupSidebarStates.value[popup.id] = { collapsed: false }
    }
    
    // Initialize panel state if not exists
    if (!popupPanelStates.value[popup.id]) {
      popupPanelStates.value[popup.id] = { collapsed: true }
    }
    
    // Initialize SQL suggestions state
    if (!showSuggestions.value[popup.id]) {
      showSuggestions.value[popup.id] = false
      selectedSuggestionIndex.value[popup.id] = -1
    }
    
    // Initialize popup scale state
    if (!popupScales.value[popup.id]) {
      popupScales.value[popup.id] = { scale: 1 } // Default 1x scale
    }
    
    // Initialize sidebar width state
    if (!popupSidebarWidths.value[popup.id]) {
      popupSidebarWidths.value[popup.id] = 256 // Default w-64 = 256px
    }
    
    // Set the newest popup as active (bring to front)
    if (popup.show && !popup.minimized) {
      activePopupId.value = popup.id
    }
    
    // Note: Quick filter for fixture_id is now handled by the quick button in TableData component
  })
}, { deep: true, immediate: true })

// Handle record selection for specific popup
const handlePopupRecordSelected = (popupId, record, columns, tableName) => {
  popupRecordViewers.value[popupId] = {
    show: true,
    record: record,
    columns: columns,
    tableName: tableName
  }
}

const handlePopupRecordDeselected = (popupId) => {
  if (popupRecordViewers.value[popupId]) {
    popupRecordViewers.value[popupId].show = false
  }
}

const closePopupRecordViewer = (popupId) => {
  handlePopupRecordDeselected(popupId)
}

const editPopupSelectedRecord = (popupId) => {
  // This will be handled by the TableData component
  if (popupRecordViewers.value[popupId]) {
    popupRecordViewers.value[popupId].show = false
  }
}

// Toggle right panel for specific popup
const togglePopupPanel = (popupId) => {
  if (!popupPanelStates.value[popupId]) {
    popupPanelStates.value[popupId] = { collapsed: true }
  }
  popupPanelStates.value[popupId].collapsed = !popupPanelStates.value[popupId].collapsed
}

// Toggle left sidebar for specific popup
const togglePopupSidebar = (popupId) => {
  if (!popupSidebarStates.value[popupId]) {
    popupSidebarStates.value[popupId] = { collapsed: true }
  }
  popupSidebarStates.value[popupId].collapsed = !popupSidebarStates.value[popupId].collapsed
}

// Handle table selection within popup
const handlePopupTableSelect = (popupId, tableName) => {
  // Find the popup and update its table
  const popup = popupStore.popupTables.find(p => p.id === popupId)
  if (popup) {
    popup.tableName = tableName
    popup.title = `${tableName} - ${popup.type === 'structure' ? 'Structure' : 'Data'}`
    
    // Clear any existing record viewer for this popup
    if (popupRecordViewers.value[popupId]) {
      popupRecordViewers.value[popupId].show = false
    }
  }
}

// Handle structure view within popup
const handlePopupStructureView = (popupId, tableName) => {
  const popup = popupStore.popupTables.find(p => p.id === popupId)
  if (popup) {
    popup.tableName = tableName
    popup.type = 'structure'
    popup.title = `${tableName} - Structure`
    
    // Clear any existing record viewer for this popup
    if (popupRecordViewers.value[popupId]) {
      popupRecordViewers.value[popupId].show = false
    }
  }
}

// Handle data view within popup
const handlePopupDataView = (popupId, tableName) => {
  const popup = popupStore.popupTables.find(p => p.id === popupId)
  if (popup) {
    popup.tableName = tableName
    popup.type = 'data'
    popup.title = `${tableName} - Data`
    
    // Clear any existing record viewer for this popup
    if (popupRecordViewers.value[popupId]) {
      popupRecordViewers.value[popupId].show = false
    }
  }
}

// Handle diagnostics toggle for specific popup
const handleDiagnosticsToggle = (popupId, data) => {
  // Create a new object to ensure reactivity
  const newDiagnostics = { ...popupDiagnostics.value }
  newDiagnostics[popupId] = {
    show: data.show,
    diagnostics: data.diagnostics,
    tableName: data.tableName,
    executionTime: data.executionTime
  }
  popupDiagnostics.value = newDiagnostics
}

// Test function to manually trigger diagnostics (for debugging)
const testDiagnostics = (popupId) => {
  const newDiagnostics = { ...popupDiagnostics.value }
  newDiagnostics[popupId] = {
    show: true,
    diagnostics: { total_time: 123, joins_enabled: false },
    tableName: 'test_table',
    executionTime: 123
  }
  popupDiagnostics.value = newDiagnostics
}

// Toggle SQL query mode for specific popup
const toggleSqlMode = (popupId) => {
  const newSqlMode = { ...popupSqlMode.value }
  if (!newSqlMode[popupId]) {
    newSqlMode[popupId] = {
      enabled: true,
      query: '',
      results: null,
      error: null,
      loading: false,
      executionTime: null
    }
    // Load SQL suggestions when enabling SQL mode
    loadSqlSuggestions()
  } else {
    newSqlMode[popupId].enabled = !newSqlMode[popupId].enabled
  }
  popupSqlMode.value = newSqlMode
}

// Handle filter state changes for specific popup and table
const handleFiltersChanged = (popupId, tableName, filters) => {
  const newTableFilters = { ...popupTableFilters.value }
  if (!newTableFilters[popupId]) {
    newTableFilters[popupId] = {}
  }
  newTableFilters[popupId][tableName] = filters
  popupTableFilters.value = newTableFilters
}

// Initialize default filter for fixture_id with current route parameter
const initializeDefaultFilter = async (popupId, tableName, fixtureId) => {
  try {
    // First check if the table has a fixture_id column
    const response = await axios.get(`/api/database-tables/${tableName}/columns`)
    const hasFixtureId = response.data.columns?.some(col => col.name === 'fixture_id')
    
    if (hasFixtureId) {
      const defaultFilter = {
        conditions: [{
          id: 1,
          enabled: false, // Not enabled by default, just pre-populated
          column: 'fixture_id',
          operator: 'equals',
          value: fixtureId,
          value2: '',
          logic: 'AND'
        }],
        showFilters: true, // Show the filters panel so user can see the pre-populated filter
        nextFilterId: 2
      }
      
      // Set the default filter state
      const newTableFilters = { ...popupTableFilters.value }
      if (!newTableFilters[popupId]) {
        newTableFilters[popupId] = {}
      }
      newTableFilters[popupId][tableName] = defaultFilter
      popupTableFilters.value = newTableFilters
    }
  } catch (error) {
    console.warn('Failed to initialize default filter:', error)
  }
}

// Toggle popup scale for specific popup
const togglePopupScale = (popupId) => {
  const currentScale = popupScales.value[popupId]?.scale || 1
  const scaleOptions = [1, 2, 3, 4]
  const currentIndex = scaleOptions.indexOf(currentScale)
  const nextIndex = (currentIndex + 1) % scaleOptions.length
  const newScale = scaleOptions[nextIndex]
  
  // Update scale state
  if (!popupScales.value[popupId]) {
    popupScales.value[popupId] = {}
  }
  popupScales.value[popupId].scale = newScale
  
  // Apply scale to popup dimensions
  const popup = popupStore.popupTables.find(p => p.id === popupId)
  if (popup) {
    // Calculate new dimensions based on scale
    const baseWidth = 800
    const baseHeight = 600
    popup.width = baseWidth * newScale
    popup.height = baseHeight * newScale
    
    // Adjust position to keep popup centered
    const centerX = Math.max(0, (window.innerWidth - popup.width) / 2)
    const centerY = Math.max(0, (window.innerHeight - popup.height) / 2)
    popup.x = centerX
    popup.y = centerY
  }
}

// Get current scale for popup
const getPopupScale = (popupId) => {
  return popupScales.value[popupId]?.scale || 1
}

// Bring popup to front
const bringPopupToFront = (popupId) => {
  activePopupId.value = popupId
}

// Get z-index for popup
const getPopupZIndex = (popupId) => {
  return activePopupId.value === popupId ? baseZIndex + 1 : baseZIndex
}

// Get chat mode index for stacking
const getChatModeIndex = (popupId) => {
  const chatModePopups = popupStore.popupTables.filter(p => p.chatMode)
  return chatModePopups.findIndex(p => p.id === popupId)
}

// Get position for minimized chat windows
const getChatMinimizedPosition = (popupId) => {
  const chatModePopups = popupStore.popupTables.filter(p => p.chatMode)
  const index = chatModePopups.findIndex(p => p.id === popupId)
  const minimizedWidth = 570
  const spacing = 10
  const rightMargin = 20
  
  return {
    x: window.innerWidth - (minimizedWidth * (index + 1)) - (spacing * index) - rightMargin,
    y: window.innerHeight - 40
  }
}

// Resize functionality for popup sidebar
const startPopupResize = (event, popupId) => {
  popupIsResizing.value[popupId] = true
  popupResizeStartX.value[popupId] = event.clientX
  popupResizeStartWidth.value[popupId] = popupSidebarWidths.value[popupId] || 256
  document.body.style.cursor = 'col-resize'
  document.body.style.userSelect = 'none'
  
  document.addEventListener('mousemove', (e) => doPopupResize(e, popupId))
  document.addEventListener('mouseup', () => stopPopupResize(popupId))
}

const doPopupResize = (event, popupId) => {
  if (!popupIsResizing.value[popupId]) return
  
  const deltaX = event.clientX - popupResizeStartX.value[popupId]
  const newWidth = popupResizeStartWidth.value[popupId] + deltaX
  
  // Set min and max width constraints
  const minWidth = 200
  const maxWidth = 600
  
  if (newWidth >= minWidth && newWidth <= maxWidth) {
    popupSidebarWidths.value[popupId] = newWidth
  }
}

const stopPopupResize = (popupId) => {
  popupIsResizing.value[popupId] = false
  document.body.style.cursor = ''
  document.body.style.userSelect = ''
  
  document.removeEventListener('mousemove', (e) => doPopupResize(e, popupId))
  document.removeEventListener('mouseup', () => stopPopupResize(popupId))
}

const validatePopupSidebarWidth = (popupId) => {
  const minWidth = 200
  const maxWidth = 600
  
  if (popupSidebarWidths.value[popupId] < minWidth) {
    popupSidebarWidths.value[popupId] = minWidth
  } else if (popupSidebarWidths.value[popupId] > maxWidth) {
    popupSidebarWidths.value[popupId] = maxWidth
  }
}

// Clear all stored state - useful for debugging/reset
const clearAllStoredState = () => {
  popupStore.clearStoredState()
  // Reset all reactive state
  Object.keys(popupPanelStates.value).forEach(key => delete popupPanelStates.value[key])
  Object.keys(popupSidebarStates.value).forEach(key => delete popupSidebarStates.value[key])
  Object.keys(popupSqlMode.value).forEach(key => delete popupSqlMode.value[key])
  Object.keys(popupTableFilters.value).forEach(key => delete popupTableFilters.value[key])
  Object.keys(popupScales.value).forEach(key => delete popupScales.value[key])
}

// Load SQL suggestions from backend
const loadSqlSuggestions = async () => {
  try {
    const response = await axios.get('/api/database-query/suggestions')
    if (response.data.success) {
      sqlSuggestions.value = response.data.suggestions
    }
  } catch (error) {
    console.warn('Failed to load SQL suggestions:', error)
  }
}

// Handle SQL input in editable div
const handleSqlInput = (popupId, event) => {
  const content = event.target.textContent || ''
  
  // Update the query state without triggering reactivity on the div content
  const newSqlMode = { ...popupSqlMode.value }
  newSqlMode[popupId] = {
    ...newSqlMode[popupId],
    query: content
  }
  popupSqlMode.value = newSqlMode
  
  // Show suggestions based on current input
  updateSuggestions(popupId, content, event.target)
}

// Handle SQL editor blur
const handleSqlBlur = (popupId) => {
  // Delay hiding suggestions to allow clicking on them
  setTimeout(() => {
    showSuggestions.value[popupId] = false
  }, 200)
}

// Handle SQL result row click
const handleSqlResultRowClick = (popupId, row) => {
  // Create columns array from the row keys
  const columns = Object.keys(row).map(key => ({
    name: key,
    type: 'mixed' // We don't have detailed type info from SQL results
  }))
  
  // Get the table name from the popup or use a generic name
  const popup = popupStore.popupTables.find(p => p.id === popupId)
  const tableName = popup?.tableName || 'SQL Results'
  
  // Trigger the record selection
  handlePopupRecordSelected(popupId, row, columns, tableName)
}

// Update suggestions based on current input
const updateSuggestions = (popupId, query, element) => {
  const words = query.toLowerCase().split(/\s+/)
  const lastWord = words[words.length - 1]
  
  if (lastWord.length < 2) {
    showSuggestions.value[popupId] = false
    return
  }
  
  const suggestions = []
  
  // SQL keywords
  const keywords = ['SELECT', 'FROM', 'WHERE', 'INSERT', 'UPDATE', 'DELETE', 'JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'INNER JOIN', 'ON', 'AS', 'AND', 'OR', 'ORDER BY', 'GROUP BY', 'HAVING', 'LIMIT', 'OFFSET', 'DISTINCT', 'COUNT', 'SUM', 'AVG', 'MAX', 'MIN', 'LIKE', 'IN', 'BETWEEN', 'IS NULL', 'IS NOT NULL', 'EXISTS', 'UNION', 'SHOW TABLES', 'DESCRIBE', 'EXPLAIN']
  
  keywords.forEach(keyword => {
    if (keyword.toLowerCase().includes(lastWord)) {
      suggestions.push({ type: 'keyword', value: keyword, description: 'SQL Keyword' })
    }
  })
  
  // Table names
  if (sqlSuggestions.value.tables) {
    sqlSuggestions.value.tables.forEach(table => {
      if (table.toLowerCase().includes(lastWord)) {
        suggestions.push({ type: 'table', value: table, description: 'Table' })
      }
    })
  }
  
  // Common queries
  if (sqlSuggestions.value.common_queries) {
    sqlSuggestions.value.common_queries.forEach(commonQuery => {
      if (commonQuery.name.toLowerCase().includes(lastWord) || commonQuery.query.toLowerCase().includes(lastWord)) {
        suggestions.push({ type: 'query', value: commonQuery.query, description: commonQuery.name })
      }
    })
  }
  
  if (suggestions.length > 0) {
    showSuggestions.value[popupId] = suggestions.slice(0, 10) // Limit to 10 suggestions
    selectedSuggestionIndex.value[popupId] = -1
  } else {
    showSuggestions.value[popupId] = false
  }
}

// Handle keydown in SQL editor
const handleSqlKeydown = (popupId, event) => {
  const suggestions = showSuggestions.value[popupId]
  
  if (suggestions && suggestions.length > 0) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      selectedSuggestionIndex.value[popupId] = Math.min(selectedSuggestionIndex.value[popupId] + 1, suggestions.length - 1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      selectedSuggestionIndex.value[popupId] = Math.max(selectedSuggestionIndex.value[popupId] - 1, -1)
    } else if (event.key === 'Tab' || event.key === 'Enter') {
      if (selectedSuggestionIndex.value[popupId] >= 0) {
        event.preventDefault()
        applySuggestion(popupId, suggestions[selectedSuggestionIndex.value[popupId]], event.target)
      }
    } else if (event.key === 'Escape') {
      showSuggestions.value[popupId] = false
    }
  }
  
  // Execute query on Ctrl+Enter or Cmd+Enter
  if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
    event.preventDefault()
    executeSqlQuery(popupId)
  }
}

// Apply selected suggestion
const applySuggestion = (popupId, suggestion, element) => {
  const currentQuery = element.textContent || ''
  const words = currentQuery.split(/\s+/)
  words[words.length - 1] = suggestion.value
  const newQuery = words.join(' ') + ' '
  
  // Update the editable div content directly
  element.textContent = newQuery
  
  // Update the query state
  const newSqlMode = { ...popupSqlMode.value }
  newSqlMode[popupId] = {
    ...newSqlMode[popupId],
    query: newQuery
  }
  popupSqlMode.value = newSqlMode
  
  // Place cursor at the end
  const range = document.createRange()
  const selection = window.getSelection()
  range.selectNodeContents(element)
  range.collapse(false)
  selection.removeAllRanges()
  selection.addRange(range)
  
  // Hide suggestions
  showSuggestions.value[popupId] = false
}

// Click suggestion
const clickSuggestion = (popupId, suggestion) => {
  const element = document.querySelector(`#sql-editor-${popupId}`)
  if (element) {
    applySuggestion(popupId, suggestion, element)
  }
}

// Syntax highlighting for SQL
const highlightSql = (query) => {
  if (!query) return ''
  
  let highlighted = query
  
  // SQL keywords
  const keywords = ['SELECT', 'FROM', 'WHERE', 'INSERT', 'UPDATE', 'DELETE', 'JOIN', 'LEFT', 'RIGHT', 'INNER', 'OUTER', 'ON', 'AS', 'AND', 'OR', 'ORDER', 'BY', 'GROUP', 'HAVING', 'LIMIT', 'OFFSET', 'DISTINCT', 'COUNT', 'SUM', 'AVG', 'MAX', 'MIN', 'LIKE', 'IN', 'BETWEEN', 'IS', 'NULL', 'NOT', 'EXISTS', 'UNION', 'CREATE', 'ALTER', 'DROP', 'TABLE', 'DATABASE', 'INDEX', 'VIEW', 'SHOW', 'DESCRIBE', 'DESC', 'EXPLAIN']
  
  keywords.forEach(keyword => {
    const regex = new RegExp(`\\b${keyword}\\b`, 'gi')
    highlighted = highlighted.replace(regex, `<span class="text-blue-600 font-semibold">${keyword}</span>`)
  })
  
  // String literals
  highlighted = highlighted.replace(/'([^']*)'/g, '<span class="text-green-600">\'$1\'</span>')
  highlighted = highlighted.replace(/"([^"]*)"/g, '<span class="text-green-600">"$1"</span>')
  
  // Numbers
  highlighted = highlighted.replace(/\b\d+\b/g, '<span class="text-purple-600">$&</span>')
  
  // Comments
  highlighted = highlighted.replace(/--.*$/gm, '<span class="text-gray-500 italic">$&</span>')
  
  return highlighted
}

// Execute SQL query for specific popup
const executeSqlQuery = async (popupId) => {
  const sqlState = popupSqlMode.value[popupId]
  if (!sqlState?.query?.trim()) return

  // Update loading state
  const newSqlMode = { ...popupSqlMode.value }
  newSqlMode[popupId] = {
    ...sqlState,
    loading: true,
    error: null,
    results: null
  }
  popupSqlMode.value = newSqlMode

  try {
    const startTime = performance.now()
    
    const response = await axios.post('/api/database/query', {
      query: sqlState.query.trim()
    })

    const endTime = performance.now()
    const executionTime = Math.round(endTime - startTime)

    const data = response.data

    // Update with results
    const updatedSqlMode = { ...popupSqlMode.value }
    updatedSqlMode[popupId] = {
      ...sqlState,
      loading: false,
      results: data.data || [],
      executionTime: data.execution_time_ms || executionTime,
      rowCount: data.row_count || (data.data ? data.data.length : 0),
      error: null
    }
    popupSqlMode.value = updatedSqlMode

  } catch (error) {
    console.error('SQL Query Error:', error)
    
    // Extract error message from axios error
    let errorMessage = 'Query execution failed'
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    } else if (error.message) {
      errorMessage = error.message
    }
    
    // Update with error
    const updatedSqlMode = { ...popupSqlMode.value }
    updatedSqlMode[popupId] = {
      ...sqlState,
      loading: false,
      error: errorMessage,
      results: null
    }
    popupSqlMode.value = updatedSqlMode
  }
}

// Clear SQL query for specific popup
const clearSqlQuery = (popupId) => {
  // Clear the contenteditable div
  const element = document.querySelector(`#sql-editor-${popupId}`)
  if (element) {
    element.textContent = ''
  }
  
  const newSqlMode = { ...popupSqlMode.value }
  if (newSqlMode[popupId]) {
    newSqlMode[popupId] = {
      ...newSqlMode[popupId],
      query: '',
      results: null,
      error: null,
      executionTime: null
    }
    popupSqlMode.value = newSqlMode
  }
}

// Remove keyboard handling from this component - moved to store
</script> 

<style scoped>
.liquid-glass-header {
  /* Base glass background with multiple layers */
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.04) 0%,
    rgba(255, 255, 255, 0.02) 25%,
    rgba(255, 255, 255, 0.01) 50%,
    rgba(255, 255, 255, 0.03) 75%,
    rgba(255, 255, 255, 0.015) 100%
  );
  
  /* Backdrop blur for glass effect */
  backdrop-filter: blur(8px) saturate(1.1);
  -webkit-backdrop-filter: blur(8px) saturate(1.1);
  
  /* Border with glass-like appearance */
  border-bottom: 1px solid rgba(209, 213, 219, 0.2);
  border-top: 1px solid rgba(255, 255, 255, 0.02);
  
  /* Box shadow for depth */
  box-shadow: 
    0 2px 8px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    inset 0 -1px 0 rgba(255, 255, 255, 0.02);
  
  /* Transition for hover effects */
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Add subtle animation */
.liquid-glass-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.04),
    transparent
  );
  animation: liquid-shimmer 3s infinite;
  pointer-events: none;
}

/* Subtle water ripple effect */
.liquid-glass-header::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
    circle at 30% 70%,
    rgba(255, 255, 255, 0.015) 0%,
    transparent 50%
  ),
  radial-gradient(
    circle at 70% 30%,
    rgba(255, 255, 255, 0.02) 0%,
    transparent 50%
  );
  animation: liquid-ripple 4s ease-in-out infinite;
  pointer-events: none;
}

/* Hover effects for interactivity */
.liquid-glass-header:hover {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.06) 0%,
    rgba(255, 255, 255, 0.03) 25%,
    rgba(255, 255, 255, 0.02) 50%,
    rgba(255, 255, 255, 0.05) 75%,
    rgba(255, 255, 255, 0.025) 100%
  );
  backdrop-filter: blur(10px) saturate(1.2);
  -webkit-backdrop-filter: blur(10px) saturate(1.2);
  box-shadow: 
    0 4px 16px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    inset 0 -1px 0 rgba(255, 255, 255, 0.04);
}

/* Shimmer animation */
@keyframes liquid-shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

/* Ripple animation */
@keyframes liquid-ripple {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.02);
  }
}

/* Add subtle distortion effect for water-like appearance */
.liquid-glass-header {
  position: relative;
}

.liquid-glass-header::before {
  z-index: -1;
}

.liquid-glass-header::after {
  z-index: -1;
}

/* Text effects for readability through the glass */
.liquid-glass-header h3,
.liquid-glass-header span,
.liquid-glass-header i {
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
}

/* Button enhancements for glass theme */
.liquid-glass-header button {
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  transition: all 0.2s ease;
}

.liquid-glass-header button:hover {
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
</style> 